/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import { io } from "socket.io-client";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { bindActionCreators } from "redux";
import moment from "moment";
import { Search, MessageSquare, Mail, MailOpen } from "lucide-react";
import RenderSupportPage from "../RenderSupportPage";
import PageSkeleton from "../../Components/PageSkeleton";
import { messageActionCreators } from "../../services/Actions";
import { SUPPORT_INBOX_DEPARTMENTS } from "../../services/Actions/MessagesAction";

// Support inbox — member-initiated conversations, mirroring /loans/messages.
//
// It shows the departments the desk fronts for (Customer Service and Loan
// Officer), not just Customer Service: members write to Loans from the member
// app, so a CS-only inbox is empty by construction. Mart Officer is currently
// commented out of SUPPORT_INBOX_DEPARTMENTS — the mart desk works its own
// inbox. Use the department chips to narrow it.
//
// These are the same conversation documents the loans inbox renders — see the
// note on SUPPORT_INBOX_DEPARTMENTS about shared read state.

// Staff-read flag moved from `system_user_read` to `is_read` at some point;
// both shapes are still in the collection.
const isChatRead = (msg) => {
  const last = msg?.chat?.at(-1);
  return last?.system_user_read ?? last?.is_read ?? false;
};

function SupportMessages() {
  const dispatch = useDispatch();
  const history = useHistory();

  const { GetSupportInboxMessages, MarkMessageAsRead } = bindActionCreators(
    messageActionCreators,
    dispatch
  );

  const message = useSelector((state) => state?.message);
  const { messages, isLoading } = message;

  const [activeFilter, setActiveFilter] = useState("all");
  const [department, setDepartment] = useState("ALL");
  const [searchTerm, setSearchTerm] = useState("");
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    Promise.resolve(GetSupportInboxMessages()).finally(() =>
      setHasLoaded(true)
    );
  }, []);

  // Live list updates: join every department room the inbox covers, same
  // contract the loans inbox uses.
  useEffect(() => {
    const socket = io("https://api.upnmg.com", {
      transports: ["websocket", "polling"],
      reconnectionAttempts: 10,
      reconnectionDelay: 2000,
    });
    const joinRooms = () =>
      SUPPORT_INBOX_DEPARTMENTS.forEach((d) =>
        socket.emit("dept:join", { department: d })
      );

    socket.on("connect", joinRooms);
    socket.on("reconnect", joinRooms);
    socket.on("dept:message_new", (update) => {
      dispatch({ type: "UPDATE_MESSAGES_LIST_ITEM", payload: update });
    });

    return () => {
      SUPPORT_INBOX_DEPARTMENTS.forEach((d) =>
        socket.emit("dept:leave", { department: d })
      );
      socket.disconnect();
    };
  }, []);

  // Department scoping is applied before the read/unread counts so the tab
  // badges describe the list actually on screen.
  const inDepartment = useMemo(
    () =>
      (messages || []).filter(
        (m) => department === "ALL" || m?.system_user === department
      ),
    [messages, department]
  );

  const counts = useMemo(() => {
    const all = inDepartment.length;
    const unread = inDepartment.filter((m) => !isChatRead(m)).length;
    return { all, unread, read: all - unread };
  }, [inDepartment]);

  // Per-department totals for the chips.
  const departmentCounts = useMemo(() => {
    const map = {};
    (messages || []).forEach((m) => {
      const key = m?.system_user || "Unknown";
      map[key] = (map[key] || 0) + 1;
    });
    return map;
  }, [messages]);

  const visible = useMemo(() => {
    let rows = [...inDepartment].sort(
      (a, b) => new Date(b.updated_at) - new Date(a.updated_at)
    );
    if (activeFilter === "unread") rows = rows.filter((m) => !isChatRead(m));
    if (activeFilter === "read") rows = rows.filter((m) => isChatRead(m));
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      rows = rows.filter((m) =>
        `${m?.userData?.staff_id ?? ""} ${m?.userData?.name ?? ""} ${m?.title ?? ""}`
          .toLowerCase()
          .includes(q)
      );
    }
    return rows;
  }, [inDepartment, activeFilter, searchTerm]);

  const openThread = (msg) => {
    const last = msg?.chat?.at(-1);
    MarkMessageAsRead(msg?._id, last?._id);
    history.push({
      pathname: "/support/messages-detailed",
      state: {
        chat_id: msg?._id,
        userData: msg?.userData,
        reference: msg?.reference,
        subject: msg?.title || msg?.subject,
        department: msg?.system_user,
      },
    });
  };

  const FILTERS = [
    { key: "all", label: "All", count: counts.all },
    { key: "unread", label: "Unread", count: counts.unread },
    { key: "read", label: "Read", count: counts.read },
  ];

  return (
    <RenderSupportPage title="Messages">
      <p className="text-sm text-slate-500 dark:text-slate-400 -mt-4 mb-4">
        Member messages across the departments the desk fronts for.
      </p>

      {/* Department scope */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {["ALL", ...SUPPORT_INBOX_DEPARTMENTS].map((d) => {
          const count = d === "ALL" ? messages?.length || 0 : departmentCounts[d] || 0;
          return (
            <button
              key={d}
              onClick={() => setDepartment(d)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                department === d
                  ? "bg-indigo-600 text-white shadow-sm"
                  : "bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-gray-700"
              }`}
            >
              {d === "ALL" ? "All departments" : d}
              <span
                className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                  department === d
                    ? "bg-white/20 text-white"
                    : "bg-slate-100 dark:bg-gray-700 text-slate-500 dark:text-slate-400"
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex gap-1 p-1 bg-slate-100 dark:bg-gray-800 rounded-lg w-fit">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setActiveFilter(f.key)}
              className={`px-4 py-2 rounded-md text-sm font-semibold transition-all flex items-center gap-2 ${
                activeFilter === f.key
                  ? "bg-white dark:bg-gray-700 text-indigo-600 dark:text-indigo-400 shadow-sm"
                  : "text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-gray-700"
              }`}
            >
              {f.label}
              {f.count > 0 && (
                <span className={`px-2 py-0.5 text-[11px] font-bold rounded-full ${
                  activeFilter === f.key
                    ? "bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300"
                    : "bg-slate-200 dark:bg-gray-700 text-slate-600 dark:text-slate-300"
                }`}>
                  {f.count}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="relative w-full md:w-72">
          <Search size={16} className="absolute left-3 top-2.5 text-slate-400" />
          <input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by staff ID or name"
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-slate-700 dark:text-slate-200 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
      </div>

      {!hasLoaded && (messages || []).length === 0 ? (
        <PageSkeleton cards={0} rows={7} cols={4} />
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 overflow-hidden">
          {visible.length === 0 ? (
            <div className="px-6 py-16 text-center">
              <MessageSquare size={30} className="mx-auto text-slate-300 dark:text-slate-600 mb-3" />
              <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">
                {activeFilter === "unread" ? "No unread messages" : "No messages yet"}
              </p>
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                {department === "ALL"
                  ? "Member messages appear here as they arrive."
                  : `No member has written to ${department}.`}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-gray-700">
              {visible.map((msg) => {
                const last = msg?.chat?.at(-1);
                const unread = !isChatRead(msg);
                return (
                  <button
                    key={msg?._id}
                    onClick={() => openThread(msg)}
                    className={`w-full text-left flex items-center gap-3 px-4 py-4 transition-colors hover:bg-slate-50 dark:hover:bg-gray-700/40 ${
                      unread ? "bg-indigo-50/60 dark:bg-indigo-900/10 border-l-4 border-l-indigo-500" : ""
                    }`}
                  >
                    <img
                      className="w-10 h-10 rounded-full object-cover border border-slate-200 dark:border-gray-600 flex-shrink-0"
                      src={msg?.userData?.profile_image || "/images/dev/avarta2.jpeg"}
                      alt="Member"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className={`text-xs truncate ${unread ? "font-black text-slate-900 dark:text-white" : "font-bold text-slate-700 dark:text-slate-300"}`}>
                          {msg?.userData?.name || msg?.title || "Member"}
                        </p>
                        <span className="px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-slate-100 dark:bg-gray-700 text-slate-500 dark:text-slate-400">
                          #{msg?.reference || msg?._id?.slice(-5)}
                        </span>
                        {/* Which department the member wrote to — the inbox
                            spans several, so the row has to say which. */}
                        <span className="px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-violet-50 dark:bg-violet-900/25 text-violet-700 dark:text-violet-300 whitespace-nowrap">
                          {msg?.system_user || "—"}
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-400 dark:text-slate-500">
                        {msg?.userData?.staff_id || "—"}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 truncate mt-0.5">
                        {last?.message}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <span className="text-[11px] text-slate-400 dark:text-slate-500">
                        {msg?.updated_at && moment(msg.updated_at).isValid()
                          ? moment(msg.updated_at).format("MMM DD")
                          : "—"}
                      </span>
                      {unread ? (
                        <Mail size={14} className="text-indigo-500" />
                      ) : (
                        <MailOpen size={14} className="text-slate-300 dark:text-slate-600" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </RenderSupportPage>
  );
}

export default SupportMessages;
